import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import apiClient from "../../../services/api";

interface EditCompetitionModalProps {
  isOpen: boolean;
  onClose: () => void;
  competitionId?: string | null;
}

interface CompetitionFormData {
  name: string;
  code: string;
  actionCode: string;
  programId: string;
  description: string;
  targetGroup: string;
  fundingMin: string;
  fundingMax: string;
  fundingPercentage: string;
  fundingCurrency: string;
  startDate: string;
  endDate: string;
  officialUrl: string;
  status: string;
}

const competitionStatusOptions = [
  { value: "ANNOUNCED", label: "Zapowiedziany" },
  { value: "ACTIVE", label: "Aktywny" },
  { value: "CLOSING", label: "Zamykany" },
  { value: "CLOSED", label: "Zamknięty" },
];

const currencyOptions = [
  { value: "PLN", label: "PLN" },
  { value: "EUR", label: "EUR" },
];

const EditCompetitionModal: React.FC<EditCompetitionModalProps> = ({
  isOpen,
  onClose,
  competitionId,
}) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<CompetitionFormData>({
    name: "",
    code: "",
    actionCode: "",
    programId: "",
    description: "",
    targetGroup: "",
    fundingMin: "",
    fundingMax: "",
    fundingPercentage: "",
    fundingCurrency: "PLN",
    startDate: "",
    endDate: "",
    officialUrl: "",
    status: "ACTIVE",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Fetch programs for dropdown
  const { data: programsData, isLoading: isLoadingPrograms } = useQuery({
    queryKey: ["grants-programs"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/programs");
      return data;
    },
    enabled: isOpen,
  });

  // Fetch competition data if editing
  const { data: competitionData, isLoading: isLoadingCompetition } = useQuery({
    queryKey: ["grants-competition", competitionId],
    queryFn: async () => {
      const { data } = await apiClient.get(
        `/grants/competitions/${competitionId}`
      );
      return data;
    },
    enabled: !!competitionId && isOpen,
  });

  // Populate form when competition data is loaded
  useEffect(() => {
    if (competitionData) {
      setFormData({
        name: competitionData.name || "",
        code: competitionData.code || "",
        actionCode: competitionData.actionCode || "",
        programId: competitionData.program?.id || "",
        description: competitionData.description || "",
        targetGroup: competitionData.targetGroup || "",
        fundingMin: competitionData.fundingRange?.min?.toString() || "",
        fundingMax: competitionData.fundingRange?.max?.toString() || "",
        fundingPercentage:
          competitionData.fundingRange?.percentage?.toString() || "",
        fundingCurrency: competitionData.fundingRange?.currency || "PLN",
        startDate: competitionData.startDate || "",
        endDate: competitionData.endDate || "",
        officialUrl: competitionData.officialUrl || "",
        status: competitionData.status || "ACTIVE",
      });
    }
  }, [competitionData]);

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiClient.post("/grants/competitions", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-competitions"] });
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      if (error.response?.status >= 500) {
        setGlobalError('Blad serwera. Sprobuj ponownie pozniej.');
      } else if (!error.response) {
        setGlobalError('Blad polaczenia. Sprawdz polaczenie z internetem.');
      } else if (error.response?.data?.details) {
        const newErrors: Record<string, string> = {};
        error.response.data.details.forEach(
          (detail: { field: string; message: string }) => {
            newErrors[detail.field] = detail.message;
          }
        );
        setErrors(newErrors);
      } else {
        setGlobalError('Wystapil nieoczekiwany blad. Sprobuj ponownie.');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiClient.put(`/grants/competitions/${competitionId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-competitions"] });
      queryClient.invalidateQueries({
        queryKey: ["grants-competition", competitionId],
      });
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      if (error.response?.status >= 500) {
        setGlobalError('Blad serwera. Sprobuj ponownie pozniej.');
      } else if (!error.response) {
        setGlobalError('Blad polaczenia. Sprawdz polaczenie z internetem.');
      } else if (error.response?.data?.details) {
        const newErrors: Record<string, string> = {};
        error.response.data.details.forEach(
          (detail: { field: string; message: string }) => {
            newErrors[detail.field] = detail.message;
          }
        );
        setErrors(newErrors);
      } else {
        setGlobalError('Wystapil nieoczekiwany blad. Sprobuj ponownie.');
      }
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      actionCode: "",
      programId: "",
      description: "",
      targetGroup: "",
      fundingMin: "",
      fundingMax: "",
      fundingPercentage: "",
      fundingCurrency: "PLN",
      startDate: "",
      endDate: "",
      officialUrl: "",
      status: "ACTIVE",
    });
    setErrors({});
    setGlobalError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setGlobalError(null);

    // Build funding range object
    const fundingRange: any = {};
    if (formData.fundingMin) fundingRange.min = parseFloat(formData.fundingMin);
    if (formData.fundingMax) fundingRange.max = parseFloat(formData.fundingMax);
    if (formData.fundingPercentage)
      fundingRange.percentage = parseFloat(formData.fundingPercentage);
    if (formData.fundingCurrency) fundingRange.currency = formData.fundingCurrency;

    const payload = {
      name: formData.name,
      code: formData.code || null,
      actionCode: formData.actionCode || null,
      programId: formData.programId,
      description: formData.description || null,
      targetGroup: formData.targetGroup || null,
      fundingRange: Object.keys(fundingRange).length > 0 ? fundingRange : null,
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
      officialUrl: formData.officialUrl || null,
      status: formData.status,
    };

    if (competitionId) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleClose = () => {
    if (!createMutation.isPending && !updateMutation.isPending) {
      resetForm();
      onClose();
    }
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !createMutation.isPending && !updateMutation.isPending) {
        resetForm();
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, createMutation.isPending, updateMutation.isPending]);

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (isOpen && !isLoadingCompetition) {
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, isLoadingCompetition]);

  if (!isOpen) return null;

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 my-8">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-medium text-neutral-900">
            {competitionId ? "Edytuj konkurs" : "Dodaj nowy konkurs"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {globalError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md" role="alert">
              <p className="text-sm text-red-700">{globalError}</p>
            </div>
          )}

          {isLoadingCompetition ? (
            <div className="text-center py-8" role="status" aria-live="polite">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">Ładowanie...</p>
            </div>
          ) : (
            <>
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Nazwa <span className="text-red-600">*</span>
                </label>
                <input
                  ref={firstInputRef}
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                    errors.name ? "border-red-500" : "border-neutral-300"
                  }`}
                  placeholder="Akademia HR"
                  maxLength={255}
                  required
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Kod konkursu
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                    placeholder="AHR-001"
                    maxLength={50}
                  />
                </div>

                {/* Action Code */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Kod działania
                  </label>
                  <input
                    type="text"
                    value={formData.actionCode}
                    onChange={(e) =>
                      setFormData({ ...formData, actionCode: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                    placeholder="FERS.01.03"
                    maxLength={20}
                  />
                </div>
              </div>

              {/* Program */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Program <span className="text-red-600">*</span>
                </label>
                <select
                  value={formData.programId}
                  onChange={(e) =>
                    setFormData({ ...formData, programId: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                    errors.programId ? "border-red-500" : "border-neutral-300"
                  }`}
                  required
                  disabled={isLoadingPrograms}
                >
                  {isLoadingPrograms ? (
                    <option value="">Ladowanie...</option>
                  ) : (
                    <>
                      <option value="">Wybierz program</option>
                      {programsData?.programs?.map((prog: any) => (
                        <option key={prog.id} value={prog.id}>
                          {prog.code} - {prog.shortName || prog.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                {errors.programId && (
                  <p className="mt-1 text-xs text-red-600">{errors.programId}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Opis
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  placeholder="Opis konkursu..."
                  maxLength={2000}
                />
              </div>

              {/* Target Group */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Grupa docelowa
                </label>
                <input
                  type="text"
                  value={formData.targetGroup}
                  onChange={(e) =>
                    setFormData({ ...formData, targetGroup: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  placeholder="MŚP, osoby bezrobotne, etc."
                  maxLength={255}
                />
              </div>

              {/* Funding Range */}
              <div className="border-t border-neutral-200 pt-4">
                <h3 className="text-sm font-medium text-neutral-900 mb-3">
                  Dofinansowanie
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Minimum
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.fundingMin}
                      onChange={(e) =>
                        setFormData({ ...formData, fundingMin: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                      placeholder="10000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Maximum
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.fundingMax}
                      onChange={(e) =>
                        setFormData({ ...formData, fundingMax: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                      placeholder="50000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Procent (0-100)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.fundingPercentage}
                      onChange={(e) =>
                        setFormData({ ...formData, fundingPercentage: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                      placeholder="80"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Waluta
                    </label>
                    <select
                      value={formData.fundingCurrency}
                      onChange={(e) =>
                        setFormData({ ...formData, fundingCurrency: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                    >
                      {currencyOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="border-t border-neutral-200 pt-4">
                <h3 className="text-sm font-medium text-neutral-900 mb-3">
                  Daty konkursu
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Data rozpoczęcia
                    </label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) =>
                        setFormData({ ...formData, startDate: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Data zakończenia
                    </label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) =>
                        setFormData({ ...formData, endDate: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>

              {/* Official URL */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Strona konkursu
                </label>
                <input
                  type="url"
                  value={formData.officialUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, officialUrl: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                    errors.officialUrl ? "border-red-500" : "border-neutral-300"
                  }`}
                  placeholder="https://akademiahr.pl"
                  maxLength={500}
                />
                {errors.officialUrl && (
                  <p className="mt-1 text-xs text-red-600">{errors.officialUrl}</p>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Status <span className="text-red-600">*</span>
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  required
                >
                  {competitionStatusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </form>

        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200 disabled:opacity-50"
          >
            Anuluj
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || isLoadingCompetition}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading && (
              <div role="status" aria-live="polite">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="sr-only">Zapisywanie...</span>
              </div>
            )}
            {isLoading
              ? "Zapisywanie..."
              : competitionId
              ? "Zaktualizuj"
              : "Dodaj konkurs"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCompetitionModal;
